import type { GeoScoreSummary } from '@lensora/shared'
import { Card, CardContent, Button } from '@lensora/ui'
import { cn } from '@lensora/ui'
import { useQuery } from '@tanstack/react-query'
import {
  Globe,
  Brain,
  FileText,
  Linkedin,
  Search,
  ArrowRight,
  X,
  CheckCircle,
  Loader2,
  Copy,
  RefreshCw,
  Lightbulb,
  ExternalLink,
  Pencil,
} from 'lucide-react'
import * as React from 'react'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'

import { FaviconImg } from '@/components/dashboard/FaviconImg'
import { ImprovementActionsSection } from '@/components/dashboard/ImprovementActionsSection'
import {
  IntelligenceSummaryBar,
  type SummaryLeaderboardEntry,
} from '@/components/dashboard/IntelligenceSummaryBar'
import { PlatformIcon } from '@/components/dashboard/PlatformIcon'
import { useActiveWebsite } from '@/hooks/use-active-website'
import { api } from '@/lib/api'

// ── Types ──────────────────────────────────────────────────────────────────────

interface LeaderboardEntry {
  rank: number
  competitor_id: string | null
  name: string
  domain: string
  favicon_url: string
  mention_rate: number
  scan_count: number
  is_own_brand: boolean
  overall_score: number
}

interface BlogPost {
  title: string
  angle: string
  why_it_wins: string
  word_count: number
}

interface SocialStrategy {
  linkedin_post: string
  reddit_thread_title: string
  reddit_thread_body: string
}

interface Intelligence {
  id: string
  competitor_id: string
  blog_strategy: BlogPost[]
  social_strategy: SocialStrategy
  quick_wins: string[]
  competitor_summary: string | null
  research_summary: string | null
  research_citations: string[]
  generated_at: string
}

interface ContentGap {
  query_text: string
  platforms: string[]
  competitor_position: number | null
}

interface BriefModal {
  post: BlogPost
  websiteName: string
  competitorName: string
  industry: string
}

const SEVEN_DAYS_MS = 7 * 24 * 60 * 60 * 1000

const ACCENT_COLORS = ['#6366f1', '#8b5cf6', '#06b6d4'] as const

// ── Markdown components (light theme) ─────────────────────────────────────────

const mdComponents = {
  h1: ({ children }: { children?: React.ReactNode }) => (
    <h1 className="mb-3 mt-4 text-xl font-bold text-text">{children}</h1>
  ),
  h2: ({ children }: { children?: React.ReactNode }) => (
    <h2 className="mb-2 mt-4 text-base font-semibold text-text">{children}</h2>
  ),
  p: ({ children }: { children?: React.ReactNode }) => (
    <p className="mb-3 text-sm leading-relaxed text-text">{children}</p>
  ),
  strong: ({ children }: { children?: React.ReactNode }) => (
    <strong className="font-semibold text-text">{children}</strong>
  ),
  ul: ({ children }: { children?: React.ReactNode }) => (
    <ul className="mb-3 list-disc pl-5 text-sm text-text">{children}</ul>
  ),
  li: ({ children }: { children?: React.ReactNode }) => <li className="mb-1">{children}</li>,
}

// ── Draft Modal ────────────────────────────────────────────────────────────────

function DraftModal({ briefModal, onClose }: { briefModal: BriefModal; onClose: () => void }) {
  const { post, websiteName, competitorName, industry } = briefModal
  const [generating, setGenerating] = React.useState(false)
  const [draft, setDraft] = React.useState<string | null>(null)
  const [error, setError] = React.useState<string | null>(null)
  const [copied, setCopied] = React.useState(false)

  async function generate() {
    setGenerating(true)
    setError(null)
    try {
      const res = await api.post<{ draft?: string }>(
        '/api/competitor-intelligence/generate-draft',
        {
          title: post.title,
          angle: post.angle,
          why_it_wins: post.why_it_wins,
          word_count: post.word_count,
          website_name: websiteName,
          competitor_name: competitorName,
          industry,
        }
      )
      setDraft(res.draft ?? '')
    } catch (err: unknown) {
      const e = err as { message?: string }
      setError(e.message ?? 'Failed to generate draft')
    } finally {
      setGenerating(false)
    }
  }

  function copyToClipboard() {
    if (!draft) return
    navigator.clipboard
      .writeText(draft)
      .then(() => {
        setCopied(true)
        setTimeout(() => setCopied(false), 2000)
      })
      .catch(() => {})
  }

  return (
    <div
      className="fixed inset-0 z-[200] flex items-center justify-center bg-black/60 p-6"
      onClick={onClose}
    >
      <div
        className="flex w-full max-w-2xl flex-col overflow-hidden rounded-xl border border-border bg-surface shadow-xl"
        style={{ maxHeight: '85vh' }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex shrink-0 items-start gap-3 border-b border-border px-5 py-4">
          <div className="min-w-0 flex-1">
            <p className="text-sm font-semibold text-text">{post.title}</p>
            <p className="mt-0.5 text-xs italic text-brand-accent">{post.angle}</p>
          </div>
          <div className="flex shrink-0 items-center gap-2">
            {draft && (
              <button
                onClick={copyToClipboard}
                className={`flex items-center gap-1.5 rounded-lg border px-3 py-1.5 text-xs transition-colors ${
                  copied
                    ? 'border-feedback-success/30 bg-feedback-success/10 text-feedback-success'
                    : 'border-border text-text-muted hover:bg-surface-overlay'
                }`}
              >
                <Copy className="h-3.5 w-3.5" />
                {copied ? 'Copied!' : 'Copy'}
              </button>
            )}
            <button
              onClick={onClose}
              className="rounded-lg p-1.5 text-text-muted hover:bg-surface-overlay"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-5">
          {!draft && !generating && !error && (
            <div className="flex flex-col items-center py-10 text-center">
              <div className="mb-4 flex h-14 w-14 items-center justify-center rounded-xl bg-brand-accent/10">
                <Pencil className="h-7 w-7 text-brand-accent" />
              </div>
              <p className="mb-1 text-sm font-semibold text-text">
                Generate a full {post.word_count.toLocaleString()}-word draft
              </p>
              <p className="mb-6 text-xs text-text-muted">Uses Claude Haiku · 1 credit</p>
              <Button onClick={generate}>Generate Full Draft (1 credit)</Button>
            </div>
          )}

          {generating && (
            <div className="flex flex-col items-center justify-center py-16 gap-3">
              <Loader2 className="h-7 w-7 animate-spin text-brand-accent" />
              <p className="text-sm text-text-muted">Generating your draft…</p>
            </div>
          )}

          {error && (
            <div className="mb-4 rounded-lg border border-feedback-error/30 bg-feedback-error/10 p-4 text-sm text-feedback-error">
              {error}
            </div>
          )}

          {draft && (
            <div className="prose max-w-none text-sm">
              <ReactMarkdown remarkPlugins={[remarkGfm]} components={mdComponents}>
                {draft}
              </ReactMarkdown>
            </div>
          )}
        </div>

        {/* Footer regenerate */}
        {draft && (
          <div className="flex shrink-0 justify-end border-t border-border px-5 py-3">
            <button
              onClick={() => {
                setDraft(null)
                generate()
              }}
              disabled={generating}
              className="flex items-center gap-1.5 rounded-lg border border-border px-3 py-1.5 text-xs text-text-muted transition-colors hover:bg-surface-overlay disabled:opacity-50"
            >
              <RefreshCw className="h-3 w-3" />
              Regenerate (1 credit)
            </button>
          </div>
        )}
      </div>
    </div>
  )
}

// ── Intelligence Overlay (bottom sheet) ───────────────────────────────────────

type OverlayTab = 'blog' | 'social' | 'gaps'

function IntelligenceOverlay({
  entry,
  intelligence,
  accentColor,
  websiteId,
  websiteName,
  websiteIndustry,
  onClose,
  onRefresh,
  refreshing,
}: {
  entry: LeaderboardEntry
  intelligence: Intelligence | null
  accentColor: string
  websiteId: string | null
  websiteName: string
  websiteIndustry: string
  onClose: () => void
  onRefresh: () => void
  refreshing: boolean
}) {
  const [tab, setTab] = React.useState<OverlayTab>('blog')
  const [gaps, setGaps] = React.useState<ContentGap[]>([])
  const [gapsLoading, setGapsLoading] = React.useState(false)
  const [gapBriefIndex, setGapBriefIndex] = React.useState<number | null>(null)
  const [draftModal, setDraftModal] = React.useState<BriefModal | null>(null)

  const isOld = intelligence
    ? Date.now() - new Date(intelligence.generated_at).getTime() > SEVEN_DAYS_MS
    : false
  const blog = intelligence?.blog_strategy ?? []
  const social = intelligence?.social_strategy
  const competitorSummary = intelligence?.competitor_summary ?? null
  const citations = (intelligence?.research_citations ?? []).filter(Boolean)

  React.useEffect(() => {
    if (tab !== 'gaps' || !websiteId || !entry.competitor_id) return
    if (gaps.length > 0) return
    setGapsLoading(true)
    api
      .get<{ gaps?: ContentGap[] }>(
        `/api/competitor-intelligence/gaps?website_id=${websiteId}&competitor_id=${entry.competitor_id}`
      )
      .then((d) => setGaps(d.gaps ?? []))
      .catch(() => {})
      .finally(() => setGapsLoading(false))
  }, [tab, websiteId, entry.competitor_id, gaps.length])

  React.useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
  }, [onClose])

  const TABS: { id: OverlayTab; icon: typeof FileText; label: string }[] = [
    { id: 'blog', icon: FileText, label: 'Blog Strategy' },
    { id: 'social', icon: Linkedin, label: 'Social Strategy' },
    { id: 'gaps', icon: Search, label: 'Content Gaps' },
  ]

  return (
    <>
      <div className="fixed inset-0 z-50 bg-black/50" onClick={onClose} aria-hidden />
      <div
        className="fixed bottom-0 left-0 right-0 z-50 mx-auto flex max-w-4xl flex-col overflow-hidden rounded-t-xl border border-b-0 border-border bg-surface shadow-2xl"
        style={{ height: '88vh' }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Accent stripe */}
        <div className="h-1 shrink-0" style={{ backgroundColor: accentColor }} />

        {/* Header */}
        <div className="flex shrink-0 items-center gap-4 border-b border-border px-6 py-4">
          <FaviconImg domain={entry.domain} name={entry.name} size={40} className="shrink-0" />
          <div className="min-w-0 flex-1">
            <p className="text-base font-bold text-text">{entry.name}</p>
            <p className="flex items-center gap-1.5 text-xs text-text-muted">
              <Globe className="h-3 w-3" />
              {entry.domain}
              <span className="ml-2 rounded-full border border-border bg-surface-raised px-2 py-0.5 text-[11px]">
                {entry.mention_rate}% mention rate
              </span>
            </p>
          </div>

          {isOld && (
            <button
              onClick={onRefresh}
              disabled={refreshing}
              className="flex items-center gap-1.5 rounded-lg border border-border px-3 py-1.5 text-xs text-text-muted transition-colors hover:bg-surface-overlay disabled:opacity-50"
            >
              {refreshing ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              ) : (
                <RefreshCw className="h-3.5 w-3.5" />
              )}
              Refresh
              <span className="rounded-full bg-feedback-warning/10 px-1.5 py-0.5 text-[10px] font-semibold text-feedback-warning">
                5 credits
              </span>
            </button>
          )}

          <button
            onClick={onClose}
            className="rounded-lg p-1.5 text-text-muted hover:bg-surface-overlay"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Competitor summary */}
        {competitorSummary && (
          <div className="shrink-0 border-b border-border bg-brand-accent/5 px-6 py-3">
            <p className="text-sm leading-relaxed text-text">
              <span className="font-semibold">Why they win: </span>
              {competitorSummary}
            </p>
          </div>
        )}

        {/* Tabs */}
        <div className="flex shrink-0 gap-2 border-b border-border px-6 py-3">
          {TABS.map(({ id, icon: Icon, label }) => (
            <button
              key={id}
              onClick={() => setTab(id)}
              className={cn(
                'flex items-center gap-1.5 rounded-lg border px-3 py-2 text-xs font-medium transition-colors',
                tab === id
                  ? 'border-brand-accent/40 bg-brand-accent/10 text-brand-accent'
                  : 'border-border text-text-muted hover:bg-surface-overlay'
              )}
            >
              <Icon className="h-3.5 w-3.5" />
              {label}
            </button>
          ))}
        </div>

        {/* Tab content */}
        <div className="flex-1 overflow-y-auto p-6">
          {!intelligence ? (
            <div className="flex h-full flex-col items-center justify-center gap-3">
              <Loader2 className="h-7 w-7 animate-spin text-brand-accent" />
              <p className="text-sm text-text-muted">Generating intelligence…</p>
            </div>
          ) : (
            <>
              {/* Blog Strategy */}
              {tab === 'blog' && (
                <div className="space-y-5">
                  <div>
                    <p className="text-[11px] font-semibold uppercase tracking-wider text-text-muted">
                      Blog Strategy
                    </p>
                    <h3 className="mt-1 text-sm font-semibold text-text">
                      Content gaps where {entry.name} wins — and how to take the lead
                    </h3>
                  </div>
                  {blog.length === 0 ? (
                    <p className="text-sm text-text-muted">No blog strategy generated.</p>
                  ) : (
                    blog.map((post, i) => (
                      <div
                        key={i}
                        className="rounded-lg border border-border bg-surface-raised p-5"
                      >
                        <div className="mb-3 flex items-start gap-3">
                          <div
                            className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg text-xs font-bold text-white"
                            style={{ backgroundColor: accentColor }}
                          >
                            {i + 1}
                          </div>
                          <div className="min-w-0 flex-1">
                            <p className="text-sm font-semibold text-text">{post.title}</p>
                            <p className="mt-0.5 text-xs italic text-brand-accent">{post.angle}</p>
                          </div>
                          <span className="shrink-0 whitespace-nowrap rounded-full border border-border px-2.5 py-0.5 text-[11px] text-text-muted">
                            {post.word_count?.toLocaleString()} words
                          </span>
                        </div>
                        <div className="mb-3 rounded-lg border border-brand-accent/20 bg-brand-accent/5 p-3">
                          <p className="mb-1 text-[11px] font-semibold uppercase tracking-wider text-brand-accent/70">
                            Why this wins
                          </p>
                          <p className="text-xs leading-relaxed text-text-muted">
                            {post.why_it_wins}
                          </p>
                        </div>
                        <button
                          onClick={() =>
                            setDraftModal({
                              post,
                              websiteName,
                              competitorName: entry.name,
                              industry: websiteIndustry,
                            })
                          }
                          className="flex items-center gap-1.5 rounded-lg border border-border px-3 py-2 text-xs text-text-muted transition-colors hover:border-brand-accent/30 hover:bg-brand-accent/5 hover:text-brand-accent"
                        >
                          <Pencil className="h-3.5 w-3.5" />
                          Generate Full Draft
                          <span className="rounded-full bg-feedback-warning/10 px-1.5 py-0.5 text-[10px] font-semibold text-feedback-warning">
                            1 credit
                          </span>
                        </button>
                      </div>
                    ))
                  )}
                </div>
              )}

              {/* Social Strategy */}
              {tab === 'social' && (
                <div className="space-y-5">
                  <div>
                    <p className="text-[11px] font-semibold uppercase tracking-wider text-text-muted">
                      Social Strategy
                    </p>
                    <h3 className="mt-1 text-sm font-semibold text-text">
                      Draft posts to drive authority and citations over {entry.name}
                    </h3>
                  </div>

                  {/* LinkedIn */}
                  <div className="overflow-hidden rounded-lg border border-border">
                    <div className="flex items-center gap-2 border-b border-border bg-[rgba(10,102,194,0.06)] px-4 py-3">
                      <Linkedin className="h-4 w-4 text-[#0A66C2]" />
                      <span className="text-sm font-semibold text-text">LinkedIn Post</span>
                    </div>
                    <div className="p-4">
                      <p className="whitespace-pre-wrap text-sm leading-relaxed text-text">
                        {social?.linkedin_post ?? 'No LinkedIn post generated.'}
                      </p>
                    </div>
                  </div>

                  {/* Reddit */}
                  <div className="overflow-hidden rounded-lg border border-border">
                    <div className="flex items-center gap-2 border-b border-border bg-[rgba(255,69,0,0.05)] px-4 py-3">
                      <span className="text-sm font-bold text-[#FF4500]">r/</span>
                      <span className="text-sm font-semibold text-text">Reddit Thread</span>
                    </div>
                    <div className="p-4">
                      {social?.reddit_thread_title && (
                        <p className="mb-3 rounded-lg border border-border bg-surface-raised px-3 py-2 text-sm font-semibold text-text">
                          {social.reddit_thread_title}
                        </p>
                      )}
                      <p className="text-sm leading-relaxed text-text">
                        {social?.reddit_thread_body ?? 'No Reddit thread generated.'}
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Content Gaps */}
              {tab === 'gaps' && (
                <div className="space-y-5">
                  <div>
                    <p className="text-[11px] font-semibold uppercase tracking-wider text-text-muted">
                      Content Gaps
                    </p>
                    <h3 className="mt-1 text-sm font-semibold text-text">
                      Queries {entry.name} wins that you don't appear in
                    </h3>
                  </div>

                  {gapsLoading ? (
                    <div className="flex items-center gap-2 text-sm text-text-muted">
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Loading gap data…
                    </div>
                  ) : gaps.length === 0 ? (
                    <div className="rounded-lg border border-dashed border-border py-10 text-center">
                      <Search className="mx-auto mb-2 h-8 w-8 text-text-muted opacity-40" />
                      <p className="text-sm text-text-muted">
                        No gaps detected yet — run more prompts to discover where competitors
                        outrank you
                      </p>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {gaps.map((gap, idx) => (
                        <div key={idx}>
                          <div className="flex items-center gap-3 rounded-lg border border-border bg-surface-raised px-4 py-3">
                            <p className="flex-1 truncate text-sm text-text" title={gap.query_text}>
                              {gap.query_text}
                            </p>
                            <div className="flex shrink-0 items-center gap-1">
                              {gap.platforms.map((p) => (
                                <PlatformIcon key={p} platform={p} size={16} />
                              ))}
                            </div>
                            <button
                              onClick={() => setGapBriefIndex(gapBriefIndex === idx ? null : idx)}
                              className="flex shrink-0 items-center gap-1 rounded-lg border border-brand-accent/30 bg-brand-accent/10 px-2.5 py-1 text-[11px] font-semibold text-brand-accent transition-colors hover:bg-brand-accent/20"
                            >
                              Create Brief <ArrowRight className="h-3 w-3" />
                            </button>
                          </div>

                          {gapBriefIndex === idx && (
                            <div className="relative mt-1 rounded-lg border border-brand-accent/20 bg-brand-accent/5 p-4">
                              <button
                                onClick={() => setGapBriefIndex(null)}
                                className="absolute right-3 top-3 text-text-muted hover:text-text"
                              >
                                <X className="h-4 w-4" />
                              </button>
                              <p className="mb-3 text-[11px] font-semibold uppercase tracking-wider text-brand-accent/70">
                                Content Brief
                              </p>
                              <div className="mb-4 space-y-2">
                                {[
                                  {
                                    label: 'Title',
                                    value: `The Complete Guide to ${gap.query_text}`,
                                  },
                                  { label: 'Target query', value: gap.query_text },
                                  {
                                    label: 'Why it matters',
                                    value: `${entry.name} appears in AI responses for this query — you don't yet.`,
                                  },
                                  { label: 'Word count', value: '1,500 words' },
                                ].map(({ label, value }) => (
                                  <div key={label} className="flex gap-3">
                                    <span className="w-28 shrink-0 text-xs font-semibold text-text-muted">
                                      {label}
                                    </span>
                                    <span className="text-xs leading-relaxed text-text">
                                      {value}
                                    </span>
                                  </div>
                                ))}
                              </div>
                              <Button
                                size="sm"
                                className="w-full"
                                onClick={() => {
                                  setGapBriefIndex(null)
                                  setDraftModal({
                                    post: {
                                      title: `The Complete Guide to ${gap.query_text}`,
                                      angle:
                                        'Write a more comprehensive, data-driven version than what currently exists.',
                                      why_it_wins: `${entry.name} ranks for "${gap.query_text}" — you don't yet.`,
                                      word_count: 1500,
                                    },
                                    websiteName,
                                    competitorName: entry.name,
                                    industry: websiteIndustry,
                                  })
                                }}
                              >
                                Generate Full Draft (1 credit)
                              </Button>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Research sources (all tabs) */}
              {citations.length > 0 && (
                <div className="mt-8 border-t border-border pt-5">
                  <p className="mb-2 flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider text-text-muted">
                    <ExternalLink className="h-3 w-3" />
                    Research sources
                  </p>
                  <div className="space-y-1">
                    {citations.map((url, i) => {
                      let displayUrl: string
                      try {
                        displayUrl = new URL(url).hostname.replace('www.', '')
                      } catch {
                        displayUrl = url
                      }
                      return (
                        <a
                          key={i}
                          href={url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center gap-1.5 text-[11px] text-text-muted hover:text-brand-accent"
                        >
                          <ExternalLink className="h-3 w-3 shrink-0" />
                          {displayUrl}
                        </a>
                      )
                    })}
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        {intelligence && (
          <div className="flex shrink-0 items-center justify-between border-t border-border px-6 py-3">
            <span className="text-[11px] text-text-muted">
              Generated{' '}
              {new Date(intelligence.generated_at).toLocaleDateString('en-GB', {
                day: 'numeric',
                month: 'short',
                year: 'numeric',
              })}
            </span>
            <div className="flex items-center gap-1.5 text-[11px] text-text-muted">
              <Lightbulb className="h-3 w-3" />
              Intelligence auto-refreshes after 7 days
            </div>
          </div>
        )}
      </div>

      {/* Draft modal */}
      {draftModal && <DraftModal briefModal={draftModal} onClose={() => setDraftModal(null)} />}
    </>
  )
}

// ── Competitor Card ────────────────────────────────────────────────────────────

function CompetitorCard({
  entry,
  intelligence,
  generating,
  accentColor,
  onClick,
}: {
  entry: LeaderboardEntry
  intelligence: Intelligence | null
  generating: boolean
  accentColor: string
  onClick: () => void
}) {
  if (generating && !intelligence) {
    return (
      <div className="flex min-h-80 flex-col overflow-hidden rounded-lg border border-border bg-surface">
        <div className="h-1.5" style={{ backgroundColor: accentColor }} />
        <div className="flex flex-1 flex-col items-center justify-center gap-4 p-6">
          <div className="h-14 w-14 animate-pulse rounded-xl bg-surface-overlay" />
          <div className="space-y-2 text-center">
            <div className="mx-auto h-4 w-36 animate-pulse rounded bg-surface-overlay" />
            <div className="mx-auto h-3 w-24 animate-pulse rounded bg-surface-overlay" />
          </div>
          <div className="flex items-center gap-2 text-xs text-text-muted">
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
            Generating intelligence…
          </div>
        </div>
      </div>
    )
  }

  return (
    <button
      onClick={onClick}
      className="flex min-h-80 w-full flex-col overflow-hidden rounded-lg border border-border bg-surface text-left transition-shadow hover:shadow-md"
    >
      <div className="h-1.5 shrink-0" style={{ backgroundColor: accentColor }} />
      <div className="flex flex-1 flex-col items-center gap-4 p-6">
        <FaviconImg domain={entry.domain} name={entry.name} size={56} className="rounded-xl" />
        <div className="text-center">
          <p className="text-lg font-bold text-text">{entry.name}</p>
          <p className="mt-1 flex items-center justify-center gap-1 text-xs text-text-muted">
            <Globe className="h-3 w-3" />
            {entry.domain}
          </p>
        </div>
        <span className="rounded-full border border-border bg-surface-raised px-4 py-1.5 text-sm font-medium text-text-muted">
          Mentioned in <span className="font-bold text-text">{entry.mention_rate}%</span> of
          responses
        </span>
        {intelligence && (
          <div className="flex items-center gap-1.5 text-xs text-feedback-success">
            <CheckCircle className="h-3.5 w-3.5" />
            Intelligence ready
          </div>
        )}
        <div
          className="mt-auto w-full rounded-lg py-2.5 text-center text-sm font-semibold text-white"
          style={{ backgroundColor: accentColor }}
        >
          View Intelligence →
        </div>
      </div>
    </button>
  )
}

// ── Main Page ──────────────────────────────────────────────────────────────────

export function RecommendationsPage() {
  const [intelligenceMap, setIntelligenceMap] = React.useState<Record<string, Intelligence>>({})
  const [generatingSet, setGeneratingSet] = React.useState<Set<string>>(new Set())
  const [selectedEntry, setSelectedEntry] = React.useState<LeaderboardEntry | null>(null)
  const [selectedAccent, setSelectedAccent] = React.useState<string>('#6366f1')
  const [refreshing, setRefreshing] = React.useState(false)

  // ── Queries ──────────────────────────────────────────────────────────────────

  const { website, websiteId } = useActiveWebsite()
  const websiteName = website?.name ?? ''
  const websiteIndustry = website?.industry ?? 'SaaS / Software'

  const { data: leaderboardData, isLoading: leaderboardLoading } = useQuery({
    queryKey: ['leaderboard', websiteId, 'all'],
    queryFn: () =>
      api.get<{ leaderboard: LeaderboardEntry[] }>(
        `/api/competitors/leaderboard?website_id=${websiteId}&period=all`
      ),
    enabled: !!websiteId,
    staleTime: 30_000,
  })

  const topCompetitors = React.useMemo(() => {
    const lb = leaderboardData?.leaderboard ?? []
    return lb.filter((e) => !e.is_own_brand && e.competitor_id).slice(0, 3)
  }, [leaderboardData])

  const { data: geoData, isLoading: geoLoading } = useQuery({
    queryKey: ['geo-score', websiteId],
    queryFn: () => api.get<GeoScoreSummary>(`/api/dashboard/geo-score?website_id=${websiteId}`),
    enabled: !!websiteId,
    staleTime: 30_000,
  })

  const ownEntry = React.useMemo(
    () => leaderboardData?.leaderboard.find((e) => e.is_own_brand) ?? null,
    [leaderboardData]
  )

  const summaryLeaderboard: SummaryLeaderboardEntry[] = React.useMemo(
    () =>
      (leaderboardData?.leaderboard ?? []).map((e) => ({
        rank: e.rank,
        name: e.name,
        visibility: e.mention_rate,
        isYou: e.is_own_brand,
      })),
    [leaderboardData]
  )

  // Auto-generate intelligence for each top competitor
  React.useEffect(() => {
    if (!websiteId || topCompetitors.length === 0) return
    topCompetitors.forEach((c) => {
      const cid = c.competitor_id!
      if (intelligenceMap[cid] || generatingSet.has(cid)) return
      api
        .get<{ intelligence: Intelligence | null }>(
          `/api/competitor-intelligence?website_id=${websiteId}&competitor_id=${cid}`
        )
        .then((d) => {
          if (d.intelligence) {
            setIntelligenceMap((prev) => ({ ...prev, [cid]: d.intelligence! }))
          } else {
            setGeneratingSet((prev) => new Set([...prev, cid]))
            api
              .post<{ intelligence?: Intelligence }>('/api/competitor-intelligence/generate', {
                website_id: websiteId,
                competitor_id: cid,
              })
              .then((g) => {
                if (g.intelligence) {
                  setIntelligenceMap((prev) => ({ ...prev, [cid]: g.intelligence! }))
                }
              })
              .catch(() => {})
              .finally(() => {
                setGeneratingSet((prev) => {
                  const n = new Set(prev)
                  n.delete(cid)
                  return n
                })
              })
          }
        })
        .catch(() => {})
    })
  }, [websiteId, topCompetitors])

  async function handleRefresh() {
    if (!selectedEntry?.competitor_id || !websiteId) return
    setRefreshing(true)
    try {
      const d = await api.post<{ intelligence?: Intelligence }>(
        '/api/competitor-intelligence/generate',
        {
          website_id: websiteId,
          competitor_id: selectedEntry.competitor_id,
          force: true,
        }
      )
      if (d.intelligence) {
        setIntelligenceMap((prev) => ({ ...prev, [selectedEntry.competitor_id!]: d.intelligence! }))
      }
    } catch {
      /* non-fatal */
    } finally {
      setRefreshing(false)
    }
  }

  // ── Render ────────────────────────────────────────────────────────────────────

  return (
    <div className="space-y-6">
      {/* Summary bar */}
      <IntelligenceSummaryBar
        visibility={geoData?.mentionRate ?? 0}
        avgPosition={geoData?.avgPosition ?? null}
        leaderboardRank={ownEntry?.rank ?? null}
        totalCompetitors={leaderboardData?.leaderboard.length ?? 0}
        leaderboard={summaryLeaderboard}
        isLoading={geoLoading || leaderboardLoading}
      />

      {/* Page header */}
      <div>
        <h2 className="flex items-center gap-2 text-2xl font-bold text-text">
          <Brain className="h-6 w-6 text-brand-accent" />
          Competitor Intelligence
        </h2>
        <p className="mt-1 text-sm text-text-muted">
          See what your top competitors are doing to win AI recommendations — and how to beat them.
        </p>
      </div>

      {/* Competitor cards grid */}
      {leaderboardLoading ? (
        <div className="grid grid-cols-1 gap-6 sm:grid-cols-3">
          {[0, 1, 2].map((i) => (
            <div
              key={i}
              className="min-h-80 animate-pulse overflow-hidden rounded-lg border border-border bg-surface"
            />
          ))}
        </div>
      ) : topCompetitors.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-16 text-center">
            <Brain className="mb-4 h-12 w-12 text-text-muted opacity-40" />
            <p className="font-semibold text-text">No competitors tracked yet</p>
            <p className="mt-1 max-w-sm text-sm text-text-muted">
              Add competitors on the Competitors page and run simulations to unlock intelligence.
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 gap-6 sm:grid-cols-3">
          {topCompetitors.map((entry, i) => {
            const accent = ACCENT_COLORS[i] ?? ACCENT_COLORS[0]
            return (
              <CompetitorCard
                key={entry.competitor_id ?? i}
                entry={entry}
                intelligence={
                  entry.competitor_id ? (intelligenceMap[entry.competitor_id] ?? null) : null
                }
                generating={entry.competitor_id ? generatingSet.has(entry.competitor_id) : false}
                accentColor={accent}
                onClick={() => {
                  setSelectedEntry(entry)
                  setSelectedAccent(accent)
                }}
              />
            )
          })}
        </div>
      )}

      <ImprovementActionsSection websiteId={websiteId} />

      {/* Full-screen overlay */}
      {selectedEntry && (
        <IntelligenceOverlay
          entry={selectedEntry}
          intelligence={
            selectedEntry.competitor_id
              ? (intelligenceMap[selectedEntry.competitor_id] ?? null)
              : null
          }
          accentColor={selectedAccent}
          websiteId={websiteId}
          websiteName={websiteName}
          websiteIndustry={websiteIndustry}
          onClose={() => setSelectedEntry(null)}
          onRefresh={handleRefresh}
          refreshing={refreshing}
        />
      )}
    </div>
  )
}
